<template>
  <section class="new-release-packages-table">
    <ElTable v-loading="fetchingData" :data="packages">
      <ElTableColumn :label="$t('APP.RELEASES.VERSION')" width="200">
        <template slot-scope="{ row: { versionCode, versionName } }">
          {{ versionCode }} ({{ versionName }})
        </template>
      </ElTableColumn>
      <ElTableColumn
        prop="targetApiLevel"
        :label="$t('APP.RELEASES.TARGET_SDK')"
        width="100"
      />
      <ElTableColumn
        prop="apiLevel"
        :label="$t('APP.RELEASES.MIN_SDK')"
        width="100"
      />
      <ElTableColumn
        v-slot="{ row: currentPackage }"
        width="180"
        :label="$t('APP.RELEASES.CPU_ARCHITECTURE')"
      >
        <ReleasePackageArchitecture
          :key="currentPackage.token"
          :package-item="currentPackage"
          :can-edit="hasEditAction"
        />
      </ElTableColumn>
      <ElTableColumn>
        <div
          slot-scope="{ row: currentPackage }"
          class="flex-container align-right"
        >
          <ElButton
            plain
            size="small"
            @click="openAppPackageDetailsModal(currentPackage)"
          >
            {{ $t('APP.RELEASES.DETAIL_PACKAGE_BTN') }}
          </ElButton>

          <LbDropdown
            v-if="hasEditAction"
            class="new-release-packages-table__more-button"
            :items="getMoreDropdownItems(currentPackage)"
          >
            <ElButton plain size="small">
              <i class="el-icon-more" />
            </ElButton>
          </LbDropdown>
        </div>
      </ElTableColumn>
    </ElTable>
  </section>
</template>

<script>
import Release from '@/api/app-releases';
import appState from '@/mixins/appState';
import modal from '@/mixins/modal';
import packageName from '@/mixins/packageName';
import LbDropdown from '@@/common/LbDropdown';
import ReleasePackageArchitecture from '@@/app/AppReleases/common/ReleasePackageArchitecture';
import NewReleasePackageExpansionsManager from './NewReleasePackageExpansionsManager';
import AppPackageDetailsModal from '@@/app/common/AppPackageDetailsModal';
import downloadFileWithUrl from '@/utils/downloadFileWithUrl';

export default {
  name: 'NewReleasePackagesTable',
  components: {
    ReleasePackageArchitecture,
    LbDropdown,
  },
  mixins: [appState, packageName, modal],
  props: {
    packages: {
      type: Array,
      required: true,
    },
    releaseId: {
      type: Number,
      required: true,
    },
    releaseActions: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fetchingData: false,
      errorList: [],
    };
  },
  computed: {
    hasEditAction() {
      return Boolean(this.releaseActions.editPackage);
    },
  },
  methods: {
    getMoreDropdownItems(packageItem) {
      return [
        {
          title: this.$t('APP.RELEASES.REMOVE_PACKAGE'),
          handler: () => this.deletePackage(packageItem),
        },
        {
          title: this.$t('APP.RELEASES.ADD_OBB'),
          handler: () => this.openObbModal(packageItem),
        },
        {
          title: this.$t('APP.RELEASES.DOWNLOAD_PACKAGE'),
          disabled: !packageItem?.isIndependentlyInstallable,
          handler: () => downloadFileWithUrl(packageItem.apkLink),
        },
      ];
    },
    openAppPackageDetailsModal(packageItem) {
      this.$modal.open({
        title: this.$t('APP.RELEASES.DETAIL_MODAL_TITLE'),
        component: AppPackageDetailsModal,
        props: { packageItem },
        width: '25%',
      });
    },
    removePackageFromRelease(removedPackage) {
      this.fetchingData = true;
      return Release.removePackageFromRelease({
        packageName: this.packageName,
        package: removedPackage,
        releaseId: this.releaseId,
      })
        .then(() => {
          this.$emit('remove-package', removedPackage);
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.fetchingData = false;
        });
    },
    deletePackage(packageItem) {
      this.$confirm(
        this.$t('APP.RELEASES.REMOVE_PACKAGE_CONFIRMATION'),
        this.$t('WARNING'),
        {
          confirmButtonText: this.$t('DELETE_IT'),
          cancelButtonText: this.$t('CANCEL'),
          cancelButtonClass: 'el-button--text is-plain',
          confirmButtonClass: 'el-button--danger',
          type: 'warning',
        },
      )
        .then(() => {
          this.removePackageFromRelease(packageItem);
        })
        .catch(() => {});
    },
    openObbModal(packageItem) {
      this.$modal.open({
        title: this.$t('APP.RELEASES.OBB'),
        component: NewReleasePackageExpansionsManager,
        props: {
          key: this.packageName,
          packageToken: packageItem.token,
        },
        width: '25%',
      });
    },
  },
};
</script>

<style lang="scss">
.new-release-packages-table {
  position: relative;

  &__title {
    color: $wet-asphalt;
    font-size: rem-calc(18);
    font-weight: 600;
    margin: 0;
  }

  &__more-button {
    margin-right: calc($global-margin / 2);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
